import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import api from "../../services/api";
import { Button } from "../../components/buttons";
import { borderColor, spacing } from "../../components/layout";

const AUTO_REFRESH_MS = 120000;
const RECENT_LIMIT = 40;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing};
  margin-bottom: 20px;
`;

const ToolbarText = styled.div`
  max-width: 720px;
  line-height: 1.45;
`;

const Muted = styled.div`
  color: var(--muted-text);
  font-size: 13px;
  margin-top: 4px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing};
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-top: 6px;
`;

const SummaryLabel = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${spacing};
  margin-bottom: 26px;
`;

const SectionCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 14px;
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 70px;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const BreakdownLabel = styled.div`
  min-width: 0;
`;

const BreakdownLabelText = styled.div`
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BreakdownBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BreakdownFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: #54d2b1;
  border-radius: 999px;
`;

const BreakdownCount = styled.div`
  text-align: right;
  font-weight: 600;
`;

const EmptyState = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const FlowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing};
`;

const FlowCard = styled.details`
  border: 1px solid ${borderColor};
  border-radius: 10px;
  background: var(--surface-bg);
  overflow: hidden;
`;

const FlowSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;

  &::-webkit-details-marker {
    display: none;
  }
`;

const UserThumb = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: #f3f3f3;
  overflow: hidden;
  flex: 0 0 52px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FlowSummaryBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const FlowTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
`;

const FlowTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => props.background || "rgba(0,0,0,0.07)"};
  color: ${props => props.color || "#1d1d1d"};
`;

const SummaryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--muted-text);
  font-size: 13px;
`;

const FlowBody = styled.div`
  border-top: 1px solid ${borderColor};
  padding: 16px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const MetaCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const MetaLabel = styled.div`
  font-size: 12px;
  color: var(--muted-text);
  margin-bottom: 6px;
`;

const MetaValue = styled.div`
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
`;

const LinkedMeetupList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

const LinkedMeetupCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const SmallText = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted-text);
  margin-top: 8px;
`;

const prettyLabel = value => {
  if (!value) {
    return "Unknown";
  }
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

const formatDateTime = value => {
  if (!value) {
    return " - ";
  }
  return new Date(value).toLocaleString();
};

const stateColors = {
  live_activated: { background: "rgba(84, 210, 177, 0.16)", color: "#0a6b53" },
  activated: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  in_window: { background: "rgba(59, 130, 246, 0.12)", color: "#1e40af" },
  upcoming: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  expired: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" },
  inactive: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" }
};

const meetupStatusColors = {
  now: { background: "rgba(84, 210, 177, 0.16)", color: "#0a6b53" },
  started: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  confirmed: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  partial: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  pending: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  cancelled: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" },
  expired: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" },
  empty: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" }
};

const BreakdownList = ({ title, items }) => {
  const maxCount = items.reduce((max, item) => Math.max(max, item.count), 0);

  return (
    <SectionCard>
      <SectionTitle>{title}</SectionTitle>
      {!items.length ? (
        <EmptyState>No data yet.</EmptyState>
      ) : (
        items.map(item => (
          <BreakdownRow key={`${title}-${item.label}`}>
            <BreakdownLabel>
              <BreakdownLabelText>{prettyLabel(item.label)}</BreakdownLabelText>
              <BreakdownBar>
                <BreakdownFill
                  width={maxCount ? Math.max((item.count / maxCount) * 100, 6) : 0}
                />
              </BreakdownBar>
            </BreakdownLabel>
            <BreakdownCount>{item.count}</BreakdownCount>
          </BreakdownRow>
        ))
      )}
    </SectionCard>
  );
};

const RecentFlowItem = ({ flow, defaultOpen }) => {
  const user = flow.user || {};
  const stateStyle = stateColors[flow.state] || stateColors.upcoming;

  return (
    <FlowCard open={defaultOpen}>
      <FlowSummary>
        <UserThumb>
          {user.profile_thumbnail_url ? <img src={user.profile_thumbnail_url} alt={user.name || "User"} /> : null}
        </UserThumb>
        <FlowSummaryBody>
          <FlowTitleRow>
            <FlowTitle>
              #{flow.id} {user.name || `User ${user.id}`}
            </FlowTitle>
            <Pill background={stateStyle.background} color={stateStyle.color}>
              {prettyLabel(flow.state)}
            </Pill>
            {flow.intent ? <Pill>{prettyLabel(flow.intent)}</Pill> : null}
            {flow.plan_type ? <Pill>{prettyLabel(flow.plan_type)}</Pill> : null}
          </FlowTitleRow>
          <SummaryMeta>
            <div>Saved {formatDateTime(flow.created_at)}</div>
            <div>
              {flow.starts_at ? formatDateTime(flow.starts_at) : "No start"} to{" "}
              {flow.ends_at ? formatDateTime(flow.ends_at) : "No end"}
            </div>
            <div>{flow.activity || flow.destination_name || "No activity / destination"}</div>
            <div>{flow.linked_ai_meetup_count || 0} linked AI meetups</div>
          </SummaryMeta>
        </FlowSummaryBody>
      </FlowSummary>

      <FlowBody>
        <MetaGrid>
          <MetaCard>
            <MetaLabel>User</MetaLabel>
            <MetaValue>
              {user.name || `User ${user.id || "-"}`}
              {user.age ? ` | ${user.age}` : ""}
              {user.approved ? " | Approved" : " | Not approved"}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Intent / Plan</MetaLabel>
            <MetaValue>
              {prettyLabel(flow.intent)} | {prettyLabel(flow.plan_type)}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Activity / Logistics</MetaLabel>
            <MetaValue>
              {prettyLabel(flow.activity)} | {prettyLabel(flow.logistics)}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Moment</MetaLabel>
            <MetaValue>{prettyLabel(flow.moment)}</MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Destination</MetaLabel>
            <MetaValue>{flow.destination_name || "No destination"}</MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Activated Live</MetaLabel>
            <MetaValue>{flow.activated_live_at ? formatDateTime(flow.activated_live_at) : "Not activated"}</MetaValue>
          </MetaCard>
        </MetaGrid>

        <SectionTitle>Linked AI Meetups</SectionTitle>
        {!flow.linked_ai_meetups.length ? (
          <EmptyState>No AI meetup linked to this completed flow yet.</EmptyState>
        ) : (
          <LinkedMeetupList>
            {flow.linked_ai_meetups.map(meetup => {
              const meetupStyle = meetupStatusColors[meetup.status] || meetupStatusColors.pending;
              const location = meetup.location || {};
              return (
                <LinkedMeetupCard key={`${flow.id}-${meetup.id}`}>
                  <FlowTitleRow>
                    <FlowTitle>Meetup #{meetup.id}</FlowTitle>
                    <Pill background={meetupStyle.background} color={meetupStyle.color}>
                      {prettyLabel(meetup.status)}
                    </Pill>
                  </FlowTitleRow>
                  <MetaValue>
                    {prettyLabel(meetup.activity)} | {prettyLabel(meetup.intent)}
                  </MetaValue>
                  <SmallText>
                    Created {formatDateTime(meetup.created_at)}
                  </SmallText>
                  <SmallText>
                    Meetup {formatDateTime(meetup.meetup_time)}
                  </SmallText>
                  <SmallText>
                    {location.name || "No location label"}
                  </SmallText>
                  <SmallText>
                    {meetup.approved_active_count || 0}/{meetup.active_participant_count || 0} active approved
                  </SmallText>
                </LinkedMeetupCard>
              );
            })}
          </LinkedMeetupList>
        )}
      </FlowBody>
    </FlowCard>
  );
};

class IntentFlow extends React.Component {
  state = {
    reload: false,
    lastRefreshedAt: null
  };

  componentDidMount() {
    this.interval = setInterval(this.refresh, AUTO_REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  loadDashboard = async () => {
    const dashboard = await api.intent_flow.dashboard({ recent_limit: RECENT_LIMIT });
    this.setState({ lastRefreshedAt: new Date() });
    return dashboard;
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  renderOverview = overview => {
    const cards = [
      { label: "Completed flows", value: overview.total_completed_flows },
      { label: "Created last 24h", value: overview.created_last_24h },
      { label: "Created last 7d", value: overview.created_last_7d },
      { label: "Active plans", value: overview.active_plan_count },
      { label: "In window now", value: overview.in_window_count },
      { label: "Activated live", value: overview.activated_live_count },
      { label: "Flows with AI meetups", value: overview.plans_with_linked_meetups },
      { label: "Linked AI meetups", value: overview.total_linked_ai_meetups }
    ];

    return (
      <SummaryGrid>
        {cards.map(card => (
          <SummaryCard key={card.label}>
            <SummaryLabel>{card.label}</SummaryLabel>
            <SummaryValue>{card.value || 0}</SummaryValue>
          </SummaryCard>
        ))}
      </SummaryGrid>
    );
  };

  renderDashboard = dashboard => {
    const breakdowns = dashboard.breakdowns || {};
    const recentFlows = dashboard.recent_flows || [];

    return (
      <React.Fragment>
        {this.renderOverview(dashboard.overview || {})}

        <SectionGrid>
          <BreakdownList title="Flow State" items={breakdowns.by_state || []} />
          <BreakdownList title="Intent (7d)" items={breakdowns.by_intent || []} />
          <BreakdownList title="Plan Type (7d)" items={breakdowns.by_plan_type || []} />
          <BreakdownList title="Activity (7d)" items={breakdowns.by_activity || []} />
          <BreakdownList title="Moment (7d)" items={breakdowns.by_moment || []} />
          <BreakdownList title="Creation Trend (7d)" items={breakdowns.created_last_7d || []} />
        </SectionGrid>

        <SectionCard>
          <SectionTitle>Recent Completed Flows</SectionTitle>
          {!recentFlows.length ? (
            <EmptyState>No completed intent flows found yet.</EmptyState>
          ) : (
            <FlowList>
              {recentFlows.map((flow, index) => (
                <RecentFlowItem
                  key={flow.id}
                  flow={flow}
                  defaultOpen={index < 3 || flow.linked_ai_meetup_count > 0}
                />
              ))}
            </FlowList>
          )}
        </SectionCard>
      </React.Fragment>
    );
  };

  render() {
    const { reload, lastRefreshedAt } = this.state;

    return (
      <Page title="Intent Flow">
        <Toolbar>
          <ToolbarText>
            Snapshot of completed intent flow saves on the backend. This view tracks the future-plan rows
            created by the live intent flow and shows whether they later produced AI meetup opportunities.
            <Muted>
              {lastRefreshedAt
                ? `Last refreshed ${lastRefreshedAt.toLocaleString()}`
                : "Waiting for first refresh"}
            </Muted>
          </ToolbarText>
          <Button onClick={this.refresh}>Refresh now</Button>
        </Toolbar>

        <Async fetchProps={this.loadDashboard} reload={reload}>
          {this.renderDashboard}
        </Async>
      </Page>
    );
  }
}

export default IntentFlow;
